/**
 * `/internal/ops/payment-providers` + `/v1/ops/payment-providers` contract types.
 *
 * Surface owner: platform-api (owner of `payment_provider_configs`,
 * migration 0016). Consumer: ops-api, proxied from `/v1/ops/payment-providers`
 * to the ops-console.
 *
 * Why this exists (PR P15). Phase 4 originally stored ZPay credentials
 * (`pid`, `key`, `submit_url`, `mapi_url`, `query_url`) as worker-env
 * `ZPAY_*` secrets. The pattern was wrong for this credential class by
 * project convention — CLAUDE.md "Do NOT" #5 says rotatable credentials
 * belong in D1 so ops-console can self-serve rotation without
 * `wrangler secret put`. These types model the DB-backed replacement.
 *
 * Append-only rotation model. Every credential change writes a NEW row
 * with `is_active=1` and flips the prior active row for the same
 * provider to `is_active=0`. History is preserved forever. `GET` returns
 * ALL rows grouped by provider so ops-console can show full lineage.
 *
 * Secret-key handling. Two separate fields:
 *   - `secret_key_masked` — always present on list responses. Shows first
 *     4 + 20 bullets + last 4 chars ("7a3f••••••••••••••••2c8e"). Safe
 *     to render anywhere in ops UI.
 *   - `secret_key` plaintext — returned ONLY by the dedicated reveal
 *     endpoint `GET /internal/ops/payment-providers/:id/reveal-key`,
 *     which writes an `ops_audit` row on every call. Ops-console UI
 *     hides this behind an explicit "Reveal" button so incidental
 *     viewing doesn't log spurious audit entries.
 */

// ─── Provider enum ─────────────────────────────────────────────────────

/**
 * Known payment providers. The DB column is free-form TEXT (no CHECK
 * constraint) so new providers land without a migration, but the
 * contracts + ops-console validator constrain writes to this set. Add a
 * new provider here when you ship its adapter.
 */
export const PAYMENT_PROVIDER_VALUES = ['zpay', 'stripe', 'wechat', 'alipay'] as const;
export type PaymentProviderValue = (typeof PAYMENT_PROVIDER_VALUES)[number];

export function isPaymentProviderValue(value: string): value is PaymentProviderValue {
  return (PAYMENT_PROVIDER_VALUES as readonly string[]).includes(value);
}

// ─── List / detail records ─────────────────────────────────────────────

/**
 * One row of `payment_provider_configs` as seen by ops-console.
 *
 * `secret_key` NEVER appears on this record. The dedicated reveal
 * endpoint is the only path that returns plaintext. `secret_key_masked`
 * is safe to render in list views, audit screens, or JSON logs.
 *
 * `mapi_url` is nullable at the column level — providers without a
 * server-side form-POST endpoint (future Stripe, etc.) don't populate
 * it. ZPayProvider still requires non-null; the platform-api factory
 * rejects a ZPay active row with `mapi_url IS NULL`.
 */
export interface PaymentProviderConfigRecord {
  readonly id: string;
  readonly provider: string;
  readonly pid: string;
  readonly secret_key_masked: string;
  readonly submit_url: string;
  readonly mapi_url: string | null;
  readonly query_url: string;
  readonly is_active: boolean;
  readonly note: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly updated_by: string | null;
}

/**
 * `GET /internal/ops/payment-providers` / `GET /v1/ops/payment-providers`
 * response.
 *
 * Returns all rows — active + retired — grouped by provider. The
 * ops-console renders each provider as a section with the active row
 * highlighted first and retired rows below. No pagination: rotations
 * happen weekly at most, total row count stays small for the lifetime
 * of the product.
 */
export interface ListPaymentProvidersResponse {
  readonly providers: readonly PaymentProviderConfigRecord[];
}

// ─── Upsert (create new record / rotate) ───────────────────────────────

/**
 * `POST /internal/ops/payment-providers` body.
 *
 * Creates a new row. When `make_active=true`, atomically retires the
 * currently-active row for the same provider in the same D1 batch (the
 * partial UNIQUE index guarantees at most one active per provider).
 *
 * Field rules:
 *   - `provider` — must be one of `PAYMENT_PROVIDER_VALUES`. Platform-api
 *     enforces; ops-console validates eagerly.
 *   - `pid`, `secret_key`, `submit_url`, `query_url` — non-empty. The
 *     server REJECTS whitespace-only values (trims, then checks length).
 *   - `mapi_url` — nullable. Platform-api REJECTS `null` when
 *     `provider='zpay'` because the ZPayProvider factory requires it.
 *     For other providers null is accepted.
 *   - `make_active` — boolean. When true, the new row lands with
 *     `is_active=1` and the prior active row (if any) is retired.
 *   - `note` — optional human context. Stored verbatim.
 *
 * Server fills `created_at / updated_at = CURRENT_TIMESTAMP` and
 * `updated_by = X-Ops-User-Id`. Client-supplied `updated_by` is
 * deliberately absent from the contract.
 */
export interface CreatePaymentProviderRequest {
  readonly provider: string;
  readonly pid: string;
  readonly secret_key: string;
  readonly submit_url: string;
  readonly mapi_url: string | null;
  readonly query_url: string;
  readonly make_active: boolean;
  readonly note?: string;
}

export interface CreatePaymentProviderResponse {
  /** The freshly-created row (masked secret). */
  readonly created: PaymentProviderConfigRecord;
  /**
   * The row that was retired to make room for the new active one, if any.
   * Null when `make_active=false`, or when the provider had no prior
   * active row (first-ever credential for that provider).
   */
  readonly retired: PaymentProviderConfigRecord | null;
}

// ─── Reveal (audit-logged plaintext) ───────────────────────────────────

/**
 * `GET /internal/ops/payment-providers/:id/reveal-key` /
 * `GET /v1/ops/payment-providers/:id/reveal-key` response.
 *
 * Returns the plaintext `secret_key` for a single row. Every successful
 * call writes an `ops_audit` row with `action = 'payment_provider.reveal'`
 * so auditors can trace who saw the plaintext and when.
 *
 * This endpoint is deliberately NOT a field on the list response —
 * ops-console must make an explicit per-row request when the operator
 * clicks "Reveal", so casual page loads don't accumulate spurious audit
 * entries.
 */
export interface RevealPaymentProviderKeyResponse {
  readonly id: string;
  readonly provider: string;
  readonly secret_key: string;
}

// ─── Guard bounds ──────────────────────────────────────────────────────

/**
 * Maximum length for user-supplied string fields. Platform-api enforces;
 * ops-console form validates eagerly.
 *
 * The numbers are comfortably larger than any realistic input — ZPay
 * keys are ~32 hex chars, URLs 60-80, notes typically one sentence.
 * The cap exists to reject absurd payloads (multi-MB strings from bug
 * or abuse) without tightening around legitimate use.
 */
export const PAYMENT_PROVIDER_PID_MAX_LENGTH = 128;
export const PAYMENT_PROVIDER_SECRET_KEY_MAX_LENGTH = 256;
export const PAYMENT_PROVIDER_URL_MAX_LENGTH = 512;
export const PAYMENT_PROVIDER_NOTE_MAX_LENGTH = 2048;
